import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { broadcastLogout, clearAllAuthStorage, setLogoutReason, touchActivity } from '../utils/sessionManager';
import {
  getAuthApiKey,
  getAuthUser,
  hasCookieSession,
  markCookieSession,
  migrateLegacyAuthStorage,
  setAuthSession,
  setCsrfToken,
  candorWebRequestAuth,
  restoreCookieSessionProfile,
  establishCookieSession,
} from '../utils/authTokenStore';
import { ssoApiPath, ssoPreferCookieSession, ssoWebClientAppId, legacyApiPath } from '../config/environments';
import { apiUrl, apiFetch, API_JSON_ACCEPT, isApiMisrouteResponse } from '../utils/apiBase';
import {
  exchangeIdeHandoffTicket,
  hasPendingIdeHandoff,
  readHandoffAuthCode,
} from '../utils/ideHandoff';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [handoffStatus, setHandoffStatus] = useState('idle');

  function cookieSessionFailureMessage() {
    try {
      const api = new URL(apiUrl('/api/v1/users/me'), window.location.origin);
      const page = new URL(window.location.href);
      if (api.origin !== page.origin) {
        return 'Secure sign-in could not finish because this site could not establish a session with the sign-in service. Please try again, or use email and password.';
      }
    } catch (_) {
      /* fall through */
    }
    return 'Secure sign-in could not be completed on this site. Please try again in a moment.';
  }

  function saveSession(apiKey, userData) {
    const { apiKey: key, user: safeUser } = setAuthSession(apiKey, userData);
    touchActivity(true);
    setToken(key || (hasCookieSession() ? 'cookie' : null));
    setUser(safeUser || userData || null);
  }

  const completeIdeHandoff = useCallback(async (source = 'auth_context') => {
    const pendingCode = readHandoffAuthCode();
    if (!pendingCode) {
      setHandoffStatus('idle');
      return { ok: false, reason: 'no_code' };
    }

    setHandoffStatus('pending');
    const result = await exchangeIdeHandoffTicket(pendingCode, { source });
    if (result.ok) {
      setHandoffStatus('success');
      if (result.cookieSession) {
        markCookieSession(true);
        touchActivity(true);
        setToken('cookie');
        setUser(result.user || null);
      } else if (result.sessionToken) {
        saveSession(result.sessionToken, result.user || null);
      } else {
        // Profile without credentials is not a signed-in session
        setHandoffStatus('failed');
        return { ok: false, reason: 'session_not_established', ...result };
      }
      return result;
    }

    setHandoffStatus('failed');
    return result;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initAuth() {
      try {
        migrateLegacyAuthStorage();
        const storedToken = getAuthApiKey();
        const parsedUser = getAuthUser();
        if (storedToken) {
          setToken(storedToken);
          if (parsedUser) {
            if (parsedUser.email && parsedUser.email.includes('candorsys.com')) {
              clearAllAuthStorage();
              setUser(null);
              setToken(null);
            } else {
              setUser(parsedUser);
            }
          }
        } else if (hasCookieSession() || ssoPreferCookieSession()) {
          const profile = await restoreCookieSessionProfile();
          if (!cancelled && profile && profile.email) {
            if (profile.email.includes('candorsys.com')) {
              clearAllAuthStorage();
              setUser(null);
              setToken(null);
            } else {
              setUser(profile);
              setToken('cookie');
            }
          } else if (hasCookieSession()) {
            markCookieSession(false);
          }
        }

        if (readHandoffAuthCode()) {
          const result = await completeIdeHandoff('auth_context_init');
          if (!cancelled && !result.ok) {
            console.warn('[AuthContext] IDE handoff exchange failed:', result.reason || result.error);
          }
        }
      } catch (err) {
        console.warn('[AuthContext] initAuth failed:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    initAuth();
    return () => { cancelled = true; };
  }, [completeIdeHandoff]);

  const login = useCallback(async (email, password) => {
    async function postLogin(path, body) {
      // ***** Code Start BH28082026_1115 - Password login via apiFetch
      const response = await apiFetch(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: API_JSON_ACCEPT,
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      let data = {};
      let raw = '';
      try {
        raw = await response.clone().text();
        data = raw ? JSON.parse(raw) : {};
      } catch (_) {
        data = {};
      }
      if (isApiMisrouteResponse(response, raw)) {
        throw new Error('api_unreachable');
      }
      return { response, data };
      // ***** Code End BH28082026_1115
    }

    let { response, data } = await postLogin(ssoApiPath('login_password'), {
      email,
      password,
      client_app_id: ssoWebClientAppId(),
    });

    const allowLegacy =
      process.env.REACT_APP_SSO_LEGACY_PASSWORD_FALLBACK === 'true';

    if (
      allowLegacy &&
      (response.status === 404 || response.status === 503)
    ) {
      ({ response, data } = await postLogin(legacyApiPath('login'), { email, password }));
    }
    if (!response.ok) {
      const detail = data.detail;
      throw new Error(
        typeof detail === 'string'
          ? detail
          : data.message || 'Invalid email or password'
      );
    }

    if (typeof data.csrf_token === 'string') {
      setCsrfToken(data.csrf_token);
    }
    if (data.session_channel === 'cookie' || data.token_type === 'cookie') {
      try {
        const profile = await establishCookieSession();
        if (!profile) {
          throw new Error(cookieSessionFailureMessage());
        }
        saveSession('', profile);
        return { apiKey: '', cookieSession: true, user: profile };
      } catch (e) {
        if (e && e.code === 'api_unreachable') {
          throw new Error(
            "Candor's sign-in service is temporarily unavailable. Please try again in a few minutes."
          );
        }
        throw e;
      }
    }

    const apiKey = data.api_key;
    const userData = data.user || null;
    if (!apiKey) throw new Error('No session returned from server');

    saveSession(apiKey, userData);
    return { apiKey, user: userData };
  }, []);

  const loginWithCloud = useCallback(async (sessionOrKey, userData) => {
    const session =
      sessionOrKey && typeof sessionOrKey === 'object' && !Array.isArray(sessionOrKey)
        ? sessionOrKey
        : { apiKey: sessionOrKey, user: userData };
    if (session.csrf_token) {
      setCsrfToken(session.csrf_token);
    }
    if (session.cookieSession) {
      try {
        const profile = await establishCookieSession();
        if (!profile) {
          throw new Error(cookieSessionFailureMessage());
        }
        saveSession('', profile);
        return { apiKey: '', cookieSession: true, user: profile };
      } catch (e) {
        if (e && e.code === 'api_unreachable') {
          throw new Error(
            "Candor's sign-in service is temporarily unavailable. Please try again in a few minutes."
          );
        }
        throw e;
      }
    }
    const key = typeof session.apiKey === 'string' ? session.apiKey.trim() : '';
    if (!key || key.length < 16 || key.length > 512) {
      throw new Error('Invalid session from sign-in');
    }
    if (!/^[A-Za-z0-9._~+-]+$/.test(key)) {
      throw new Error('Invalid session from sign-in');
    }
    if (key.split('.').length === 3 && key.length > 80) {
      throw new Error('Invalid session from sign-in');
    }
    saveSession(key, session.user && typeof session.user === 'object' ? session.user : userData || null);
    return { apiKey: key, user: session.user || userData };
  }, []);

  const logout = useCallback(async () => {
    const key = token && token !== 'cookie' ? token : getAuthApiKey();
    const auth = candorWebRequestAuth({ 'Content-Type': 'application/json' });
    try {
      // ***** Code Start BH28082026_1115 - Logout via apiFetch
      await apiFetch(ssoApiPath('logout'), {
        method: 'POST',
        ...auth,
        headers: {
          ...(auth.headers || {}),
          Accept: API_JSON_ACCEPT,
        },
      });
      // ***** Code End BH28082026_1115
    } catch (_) {
      /* ignore */
    }
    const looksJwt = typeof key === 'string' && key.split('.').length === 3 && key.length > 80;
    if (key && !looksJwt) {
      try {
        await apiFetch(legacyApiPath('logout'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: API_JSON_ACCEPT,
            'X-API-Key': key,
            ...(auth.headers['X-CSRF-Token'] ? { 'X-CSRF-Token': auth.headers['X-CSRF-Token'] } : {}),
          },
          credentials: 'include',
        });
      } catch (_) {
        /* local clear still runs */
      }
    }
    clearAllAuthStorage();
    setToken(null);
    setUser(null);
    setHandoffStatus('idle');
    try {
      broadcastLogout('user');
    } catch (_) {}
  }, [token]);

  useEffect(() => {
    const handleUnauthorized = () => {
      setLogoutReason('unauthorized');
      broadcastLogout('unauthorized');
      logout();
      window.location.hash = '#/login?timeout=true';
    };

    window.addEventListener('candor-unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('candor-unauthorized', handleUnauthorized);
    };
  }, [logout]);

  const isAuthenticated = Boolean(token) || hasCookieSession();
  const handoffPending =
    handoffStatus === 'pending' ||
    (hasPendingIdeHandoff() && !isAuthenticated && handoffStatus !== 'failed');

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        handoffStatus,
        handoffPending,
        isAuthenticated,
        login,
        loginWithCloud,
        logout,
        completeIdeHandoff,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
